import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Mail, UserCog, Trash2, RefreshCw, Settings, Copy, Check, Loader2, Navigation } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useCompanyUsers, useCompanyUsersRealtime } from "@/hooks/useCompanyUsers";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { useValidateUserLimit } from "@/hooks/useCompanyPlans";
import { SystemAccessDisplay } from "@/components/company/SystemAccessDisplay";
import { UserSystemAssignmentModal } from "@/components/company/UserSystemAssignmentModal";
import { SystemSelector } from "@/components/company/SystemSelector";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { GpsUserPermissionsModal } from "@/components/company/GpsUserPermissionsModal";

// Helper function to get role label
/**
 * ROLES DISPONÍVEIS NA EMPRESA:
 * 
 * - company_admin: Administrador (acesso total)
 * - company_manager: Gerente (gerenciamento geral)
 * - company_user: Usuário (acesso básico)
 * - module_user: Usuário de Módulo (acesso via módulos específicos)
 * - fleet_viewer: Visualizador (apenas visualização do Fleet)
 * - fleet_manager: Gestor de Frotas (gerenciamento completo do Fleet, aprovações)
 * - fleet_ops_manager: Gestor de Operações (operações do Fleet, sem abastecimento)
 */
const getRoleLabel = (role: string): string => {
  const roleMap: Record<string, string> = {
    'company_admin': 'Administrador',
    'company_manager': 'Gerente',
    'company_user': 'Usuário',
    'module_user': 'Usuário de Módulo',
    'fleet_viewer': 'Visualizador',
    'fleet_manager': 'Gestor de Frotas',
    'fleet_ops_manager': 'Gestor de Operações'
  };
  return roleMap[role] || role;
};

// Helper function to get role variant for Badge
const getRoleVariant = (role: string): 'default' | 'secondary' | 'outline' => {
  if (role === 'company_admin') return 'default';
  if (role === 'company_manager') return 'default';
  if (role === 'fleet_manager') return 'default';
  if (role === 'fleet_ops_manager') return 'default';
  return 'secondary';
};

export default function CompanyUsersPage() {
  const { slug } = useParams();
  const { companies } = useAuth();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: "",
    fullName: "",
    role: "company_user",
    selectedSystems: [] as string[]
  });
  const [resendingInvitations, setResendingInvitations] = useState<string[]>([]);
  const [deletingInvitations, setDeletingInvitations] = useState<string[]>([]);
  const [isCreatingUser, setIsCreatingUser] = useState(false);

  // Edit user modal state
  const [editUserModal, setEditUserModal] = useState<{
    isOpen: boolean
    user: any | null
    newRole: string
    loading: boolean
  }>({
    isOpen: false,
    user: null,
    newRole: '',
    loading: false
  });

  // Remove user modal state
  const [removeUserModal, setRemoveUserModal] = useState<{
    isOpen: boolean
    user: any | null
    loading: boolean
  }>({
    isOpen: false,
    user: null,
    loading: false
  });

  // System assignment modal state
  const [systemAssignmentModal, setSystemAssignmentModal] = useState<{
    isOpen: boolean
    user: any | null
  }>({
    isOpen: false,
    user: null
  });

  // GPS permissions modal state
  const [gpsPermissionsModal, setGpsPermissionsModal] = useState<{
    isOpen: boolean
    user: any | null
  }>({
    isOpen: false,
    user: null
  });


  const company = companies?.find(c => c.slug === slug);

  // Use the new surgical hook
  const { data: users = [], isLoading, refetch } = useCompanyUsers(company?.id || '', 'all');
  const { invalidateUsers } = useCompanyUsersRealtime(company?.id || '');
  
  // Check user limits based on plans
  const { data: userLimits } = useValidateUserLimit(company?.id);

  // Debug logging
  useEffect(() => {
    if (users && users.length > 0) {
      console.log('🎯 CompanyUsersPage - Users rendered:', users.length);
      console.table(users.map(u => ({ 
        name: u.name, 
        email: u.email, 
        role: u.role,
        user_id: u.user_id 
      })));
    }
  }, [users]);

  const { data: invitations, refetch: refetchInvitations } = useQuery({
    queryKey: ['company-invitations', company?.id],
    queryFn: async () => {
      if (!company?.id) return [];
      
      const { data, error } = await supabase
        .from('company_invitations')
        .select('*')
        .eq('company_id', company.id)
        .eq('status', 'sent');

      if (error) throw error;
      return data || [];
    },
    enabled: !!company?.id,
  });

  const handleCreateUser = async () => {
    if (!company?.id || !inviteForm.email || !inviteForm.fullName) return;

    // Check user limits
    if (!userLimits?.canAddUser) {
      toast({
        title: "Limite de usuários excedido",
        description: `O plano atual permite apenas ${userLimits?.limit} usuários. Atualize seu plano para adicionar mais usuários.`,
        variant: "destructive",
      });
      return;
    }

    setIsCreatingUser(true);
    try {
      // Create user directly via Edge Function
      const { data, error } = await supabase.functions.invoke('create-company-user', {
        body: {
          email: inviteForm.email,
          fullName: inviteForm.fullName,
          role: inviteForm.role,
          companyId: company.id,
          systemIds: inviteForm.selectedSystems
        }
      });

      if (error || !data?.success) {
        throw new Error(data?.error || 'Erro ao criar usuário');
      }

      console.log('User created successfully:', data);

      // Show success message with temporary password
      if (data.tempPassword) {
        const tempPassword = data.tempPassword;
        let copiedToClipboard = false;

        const copyToClipboard = () => {
          navigator.clipboard.writeText(tempPassword);
          copiedToClipboard = true;
        };

        toast({
          title: "Usuário criado com sucesso!",
          description: (
            <div className="space-y-2">
              <p>{data.emailSent ? `✅ Email enviado para ${inviteForm.email} com as credenciais.` : '⚠️ Usuário criado, mas houve problema no envio do email.'}</p>
              <div className="bg-muted p-2 rounded text-sm">
                <p className="font-medium">Senha temporária:</p>
                <div className="flex items-center gap-2">
                  <code className="bg-background px-2 py-1 rounded">{tempPassword}</code>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={copyToClipboard}
                    className="h-6 w-6 p-0"
                  >
                    {copiedToClipboard ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  </Button>
                </div>
              </div>
            </div>
          ),
          duration: 10000,
        });
      } else {
        toast({
          title: "Sucesso!",
          description: data.message || "Usuário criado com sucesso.",
        });
      }

      setInviteForm({ 
        email: "", 
        fullName: "", 
        role: "company_user",
        selectedSystems: []
      });
      setIsInviteOpen(false);
      
      // Invalidate cache for fresh data
      invalidateUsers();
      queryClient.invalidateQueries({ queryKey: ['user-limit', company?.id] });
    } catch (error: any) {
      toast({
        title: "Erro ao criar usuário",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handleResendInvite = async (invitation: any) => {
    if (!company?.id) return;

    setResendingInvitations(prev => [...prev, invitation.id]);

    try {
      const { error: emailError } = await supabase.functions.invoke('send-invitation-email', {
        body: {
          email: invitation.email,
          fullName: invitation.full_name,
          companyName: company.name,
          role: invitation.role,
          inviterName: user?.user_metadata?.full_name || user?.email || 'Sistema',
          invitationId: invitation.id
        }
      });

      if (emailError) {
        console.error('Error sending email:', emailError);
        toast({
          title: "Erro ao reenviar convite",
          description: "Houve erro no envio do email.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Convite reenviado",
          description: `Convite reenviado por email para ${invitation.email}`,
        });
      }
    } catch (error: any) {
      toast({
        title: "Erro ao reenviar convite",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setResendingInvitations(prev => prev.filter(id => id !== invitation.id));
    }
  };

  const handleDeleteInvite = async (invitation: any) => {
    if (!company?.id) return;

    setDeletingInvitations(prev => [...prev, invitation.id]);

    try {
      const { error } = await supabase
        .from('company_invitations')
        .delete()
        .eq('id', invitation.id);

      if (error) throw error;

      toast({
        title: "Convite removido",
        description: `Convite para ${invitation.email} foi removido com sucesso.`,
      });

      // Invalidate cache for fresh data
      invalidateUsers();
      refetchInvitations();
    } catch (error: any) {
      toast({
        title: "Erro ao remover convite",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeletingInvitations(prev => prev.filter(id => id !== invitation.id));
    }
  };

  // Handle edit user role
  const handleEditUser = (user: any) => {
    setEditUserModal({
      isOpen: true,
      user,
      newRole: user.role,
      loading: false
    });
  };

  const handleSaveUserRole = async () => {
    if (!editUserModal.user || !editUserModal.newRole) return;

    setEditUserModal(prev => ({ ...prev, loading: true }));

    try {
      const { error } = await supabase
        .from('user_companies')
        .update({ role: editUserModal.newRole })
        .eq('company_id', company?.id)
        .eq('user_id', editUserModal.user.user_id);

      if (error) throw error;

      const roleLabel = getRoleLabel(editUserModal.newRole);
      toast({
        title: "Role atualizado",
        description: `Role do usuário alterado para ${roleLabel}.`,
      });

      invalidateUsers();
      setEditUserModal({
        isOpen: false,
        user: null,
        newRole: '',
        loading: false
      });
    } catch (error: any) {
      console.error('Error updating user role:', error);
      toast({
        title: "Erro",
        description: "Erro ao atualizar o role do usuário. Tente novamente.",
        variant: "destructive",
      });
      setEditUserModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Handle system assignment
  const handleManageSystems = (user: any) => {
    setSystemAssignmentModal({
      isOpen: true,
      user
    });
  };

  // Handle GPS permissions
  const handleGpsPermissions = (user: any) => {
    setGpsPermissionsModal({
      isOpen: true,
      user
    });
  };

  // Check if user has GPS access
  const { data: userSystemsAccess } = useQuery({
    queryKey: ['user-systems-access-batch', company?.id, users],
    queryFn: async () => {
      if (!company?.id || !users?.length) return {};
      
      const userIds = users.map(u => u.user_id);
      const { data, error } = await supabase
        .from('user_system_access' as any)
        .select('user_id, system_id, systems(code)')
        .eq('company_id', company.id)
        .in('user_id', userIds);
      
      if (error) {
        console.error('Error fetching user systems access:', error);
        return {};
      }
      
      // Group by user_id and check for GPS
      const accessMap: Record<string, boolean> = {};
      data?.forEach((access: any) => {
        if (access.systems?.code === 'GPS_TRACKING') {
          accessMap[access.user_id] = true;
        }
      });
      
      return accessMap;
    },
    enabled: !!company?.id && !!users?.length,
  });

  // Handle remove user
  const handleRemoveUser = (user: any) => {
    setRemoveUserModal({
      isOpen: true,
      user,
      loading: false
    });
  };

  const handleConfirmRemoveUser = async () => {
    if (!removeUserModal.user) return;

    setRemoveUserModal(prev => ({ ...prev, loading: true }));

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;

      const { data, error } = await supabase.functions.invoke('delete-company-user', {
        body: {
          userId: removeUserModal.user.user_id,
          companyId: company?.id
        },
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      });

      if (error) {
        throw error;
      }
      if (!data?.success) {
        throw new Error(data?.error || 'Erro ao excluir usuário');
      }

      toast({
        title: "Usuário excluído",
        description: data.message || "Usuário excluído completamente do sistema.",
      });

      invalidateUsers();
      queryClient.invalidateQueries({ queryKey: ['user-limit', company?.id] });
      setRemoveUserModal({
        isOpen: false,
        user: null,
        loading: false
      });
    } catch (error: any) {
      console.error('Error deleting user:', error);
      const status = error?.status ?? error?.context?.response?.status;
      let message = "Erro ao excluir usuário. Tente novamente.";
      if (status === 401) message = 'Não autorizado. Faça login novamente.';
      else if (status === 403) message = 'Você não tem permissão para excluir usuários.';
      else if (status === 404) message = 'Usuário não encontrado nesta empresa.';
      else if (status === 400 && (error?.message?.toLowerCase?.().includes('last admin'))) {
        message = 'Não é possível excluir o último administrador da empresa.';
      }
      toast({
        title: "Erro",
        description: message,
        variant: "destructive",
      });
      setRemoveUserModal(prev => ({ ...prev, loading: false }));
    }
  };
  if (isLoading) {
    return <div className="flex items-center justify-center h-64">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Usuários</h1>
          <p className="text-muted-foreground">
            Gerencie os usuários da empresa {company?.name}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => {
              invalidateUsers();
              refetchInvitations();
            }}
            size="sm"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
          <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Criar Usuário
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Criar Novo Usuário</DialogTitle>
                <DialogDescription>
                  Crie um usuário diretamente no sistema com acesso aos sistemas selecionados.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                      placeholder="usuario@exemplo.com"
                    />
                  </div>
                  <div className="col-span-2">
                    <Alert>
                      <AlertDescription>
                        Uma senha temporária será gerada automaticamente e enviada por email para o usuário. O usuário será obrigado a criar uma nova senha no primeiro acesso.
                      </AlertDescription>
                    </Alert>
                  </div>
                </div>
                <div>
                  <Label htmlFor="fullName">Nome Completo</Label>
                  <Input
                    id="fullName"
                    value={inviteForm.fullName}
                    onChange={(e) => setInviteForm({ ...inviteForm, fullName: e.target.value })}
                    placeholder="Nome do usuário"
                  />
                </div>
                <div>
                  <Label htmlFor="role">Função</Label>
                  <Select value={inviteForm.role} onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="company_admin">Administrador</SelectItem>
                      <SelectItem value="company_manager">Gerente</SelectItem>
                      <SelectItem value="company_user">Usuário</SelectItem>
                      <SelectItem value="module_user">Usuário de Módulo</SelectItem>
                      <SelectItem value="fleet_viewer">Visualizador</SelectItem>
                      <SelectItem value="fleet_manager">Gestor de Frotas</SelectItem>
                      <SelectItem value="fleet_ops_manager">Gestor de Operações</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Sistemas Liberados</Label>
                  <SystemSelector
                    companyId={company?.id || ''}
                    selectedSystems={inviteForm.selectedSystems}
                    onSystemsChange={(systemIds) => setInviteForm({ ...inviteForm, selectedSystems: systemIds })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => setIsInviteOpen(false)}
                  disabled={isCreatingUser}
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={handleCreateUser}
                  disabled={isCreatingUser || !inviteForm.email || !inviteForm.fullName}
                >
                  {isCreatingUser ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  {isCreatingUser ? "Criando..." : "Criar Usuário"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid gap-6">
        {/* Active Users */}
        <Card>
          <CardHeader>
            <CardTitle>
              Usuários Ativos ({users?.length || 0}
              {userLimits && ` / ${userLimits.limit}`})
            </CardTitle>
            <CardDescription>
              Usuários que têm acesso aos sistemas incluídos nos planos da empresa
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Função</TableHead>
                  <TableHead>Acesso a Sistemas</TableHead>
                  <TableHead>Data de Entrada</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users?.length === 0 ? (
                  <TableRow>
                     <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                       Nenhum usuário encontrado para esta empresa
                     </TableCell>
                  </TableRow>
                ) : (
                   users?.map((user) => {
                     return (
                     <TableRow key={user.user_id}>
                       <TableCell>
                         <div className="flex items-center space-x-3">
                           <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                             {user.name?.[0] || '?'}
                           </div>
                           <span>{user.name || 'Nome não disponível'}</span>
                         </div>
                       </TableCell>
                       <TableCell>
                         <span className="text-sm text-muted-foreground">{user.email}</span>
                       </TableCell>
                         <TableCell>
                           <Badge variant={getRoleVariant(user.role)}>
                             {getRoleLabel(user.role)}
                           </Badge>
                         </TableCell>
                       <TableCell>
                         <SystemAccessDisplay userId={user.user_id} companyId={company?.id} />
                       </TableCell>
                       <TableCell>
                         {new Date(user.created_at).toLocaleDateString('pt-BR')}
                       </TableCell>
                         <TableCell>
                           <div className="flex items-center space-x-2">
                             <Button 
                               variant="ghost" 
                               size="sm"
                               onClick={() => handleEditUser(user)}
                               title="Editar role"
                             >
                               <UserCog className="h-4 w-4" />
                             </Button>
                             <Button 
                               variant="ghost" 
                               size="sm"
                               onClick={() => handleManageSystems(user)}
                               title="Gerenciar sistemas"
                             >
                               <Settings className="h-4 w-4" />
                             </Button>
                             {userSystemsAccess?.[user.user_id] && (
                               <Button 
                                 variant="ghost" 
                                 size="sm"
                                 onClick={() => handleGpsPermissions(user)}
                                 title="Permissões GPS"
                                 className="text-primary"
                               >
                                 <Navigation className="h-4 w-4" />
                               </Button>
                             )}
                             <Button 
                               variant="ghost" 
                               size="sm"
                               onClick={() => handleRemoveUser(user)}
                              title="Remover usuário"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                     </TableRow>
                   )
                   })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Pending Invitations */}
        {invitations && invitations.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Convites Pendentes</CardTitle>
              <CardDescription>
                Convites enviados que ainda não foram aceitos
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Função</TableHead>
                    <TableHead>Enviado em</TableHead>
                    <TableHead>Expira em</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invitations.map((invitation) => (
                    <TableRow key={invitation.id}>
                      <TableCell>{invitation.email}</TableCell>
                      <TableCell>{invitation.full_name || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {getRoleLabel(invitation.role)}
                          </Badge>
                        </TableCell>
                      <TableCell>
                        {new Date(invitation.created_at).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell>
                        {new Date(invitation.expires_at).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResendInvite(invitation)}
                            disabled={resendingInvitations.includes(invitation.id)}
                            title="Reenviar convite"
                          >
                            <RefreshCw className={`h-4 w-4 ${resendingInvitations.includes(invitation.id) ? 'animate-spin' : ''}`} />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={deletingInvitations.includes(invitation.id)}
                                title="Remover convite"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remover convite</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Tem certeza que deseja remover o convite para <strong>{invitation.email}</strong>?
                                  Esta ação não pode ser desfeita.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteInvite(invitation)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Remover
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit User Role Modal */}
      <Dialog open={editUserModal.isOpen} onOpenChange={(open) => 
        !editUserModal.loading && setEditUserModal(prev => ({ ...prev, isOpen: open }))
      }>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Role do Usuário</DialogTitle>
            <DialogDescription>
              Altere o role de {editUserModal.user?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="userRole">Role</Label>
              <Select 
                value={editUserModal.newRole} 
                onValueChange={(value) => setEditUserModal(prev => ({ ...prev, newRole: value }))}
                disabled={editUserModal.loading}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="company_admin">Administrador</SelectItem>
                  <SelectItem value="company_manager">Gerente</SelectItem>
                  <SelectItem value="company_user">Usuário</SelectItem>
                  <SelectItem value="module_user">Usuário de Módulo</SelectItem>
                  <SelectItem value="fleet_viewer">Visualizador</SelectItem>
                  <SelectItem value="fleet_manager">Gestor de Frotas</SelectItem>
                  <SelectItem value="fleet_ops_manager">Gestor de Operações</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditUserModal(prev => ({ ...prev, isOpen: false }))}
              disabled={editUserModal.loading}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveUserRole}
              disabled={editUserModal.loading}
            >
              {editUserModal.loading ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove User Modal */}
      <AlertDialog open={removeUserModal.isOpen} onOpenChange={(open) =>
        !removeUserModal.loading && setRemoveUserModal(prev => ({ ...prev, isOpen: open }))
      }>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Usuário Permanentemente</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-2">
                <p>
                  Tem certeza que deseja <strong>excluir permanentemente</strong> o usuário <strong>{removeUserModal.user?.name}</strong>?
                </p>
                <div className="bg-destructive/10 border border-destructive/20 rounded p-3 text-sm">
                  <p className="font-medium text-destructive">⚠️ ATENÇÃO:</p>
                  <ul className="list-disc list-inside mt-1 text-destructive/80">
                    <li>O usuário será completamente removido do sistema</li>
                    <li>Todas as associações com empresas e sistemas serão perdidas</li>
                    <li>O acesso de login será revogado permanentemente</li>
                    <li>Esta ação não pode ser desfeita</li>
                  </ul>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeUserModal.loading}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRemoveUser}
              disabled={removeUserModal.loading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removeUserModal.loading ? "Excluindo..." : "Excluir Permanentemente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* System Assignment Modal */}
      <UserSystemAssignmentModal
        open={systemAssignmentModal.isOpen}
        onClose={() => setSystemAssignmentModal({ isOpen: false, user: null })}
        user={systemAssignmentModal.user}
        companyId={company?.id || ''}
        onSuccess={() => {
          invalidateUsers();
          queryClient.invalidateQueries({ queryKey: ['user-systems-access-batch'] });
          setSystemAssignmentModal({ isOpen: false, user: null });
        }}
      />

      {/* GPS Permissions Modal */}
      {gpsPermissionsModal.user && (
        <GpsUserPermissionsModal
          open={gpsPermissionsModal.isOpen}
          onClose={() => setGpsPermissionsModal({ isOpen: false, user: null })}
          userId={gpsPermissionsModal.user.user_id}
          userName={gpsPermissionsModal.user.name}
          companyId={company?.id || ''}
        />
      )}

    </div>
  );
}